import React, { useState, useEffect } from "react";
import { fetchItems } from "../../utils/fetchItemData";
import { useModalStack } from "../../utils/modalContext";
import { ItemMarket } from "./ItemMarket";
import { ItemCrafting } from "./ItemCrafting";
import { ItemSpritesheet } from "./itemSpritesheet";
import { ItemCrop } from "./itemCrop";
import { CraftMap } from "./itemCraftMap";
import { ItemProduction } from "./itemProduction";
import { useCurrencies } from "../../hooks/useCurrencies";

export function ItemDetails({ item }) {
  const [currencyData, setCurrencyData] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const { pushModal } = useModalStack();
  const { data } = useCurrencies();

  useEffect(() => {
    const fetchData = async () => {
      if (data && item.stores && item.stores.length > 0) {
        const curData = data.find((c) => c.id === item.stores[0].currency);
        setCurrencyData(curData);
      }
    };
    fetchData();
  }, [item.stores, data]);

  useEffect(() => {
    if (!item) return;

    // Auto-select the first available tab
    if (item.type === "Item" && item.recipes?.length > 0) {
      setActiveTab("crafting");
    } else if (item.type === "Item" && item.crop) {
      setActiveTab("crop");
    } else if (item.type === "Item" && item.produce?.length > 0) {
      setActiveTab("produce");
    } else if (item.type === "Item" && item.recipes?.length > 0) {
      setActiveTab("craftMap");
    } else if (item.placeObject && item.placeObject.image) {
      setActiveTab("sprite");
    } else {
      setActiveTab("market");
    }
  }, [item]);

  const handleOpenModal = async (itemId) => {
    try {
      const itemData = await fetchItems({ itemId });
      if (!itemData || !itemData[0]) return;

      pushModal({
        id: `item-${itemId}`,
        type: "itemModal",
        props: {
          item: itemData[0],
          onClose: () => {},
          showNavigation: false,
          onNavigate: null,
          totalItems: 1,
          index: null,
        },
      });
    } catch (err) {
      console.error("Failed to fetch item:", err);
    }
  };

  if (!item) return null;

  return (
    <div className="mb-6">
      <div className="wiki-details">
        <h2 className="wiki-name">{item.name}</h2>
        <div className="wiki-icon">
          <img src={item.image} alt={item.name} className="w-16 h-16 mx-auto md:mx-0" />
          <div className="w-full">
            <div className="bg-[var(--button3-bg-color)] p-2">
              <p className="text-[var(--text-color)]">
                <span className="item-title">🧾Pixels Description:</span> {item.description || "This item has no game description"}
              </p>
              {item.utility && (
                <p className="text-[var(--text-color)]">
                  <span className="item-title">ℹ️ Utility:</span> {item.utility}
                </p>
              )}
            </div>

            {item.notes?.[0]?.description && (
              <div className="bg-[var(--button3-bg-color)] p-2 mt-2">
                <p className="text-[var(--text-color)]">
                  <span className="item-title">🌟 PixeLore Description:</span> {item.notes[0].description}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col md:flex-row">
          <div className="wiki-items">
            <p className="item-line overflow-hidden">
              <span className="item-title">ID:</span> {item.id}
            </p>
            <p className="item-line">
              <span className="item-title">Tier:</span> {item.itemTier}
            </p>
            {item.requirements && (
              <p className="item-line">
                <span className="item-title">Requirements:</span> {`${item.requirements.levelType} Level ${item.requirements.level}`}
              </p>
            )}
            <p className="item-line">
              <span className="item-title">Item Tradable:</span> {item.tradeable}
            </p>
            <p className="item-line">
              <span className="item-title">Item Rarity:</span>{" "}
              {item.notes?.[0]?.limited ? `Limited (Total: ${item.notes?.[0]?.exist ? item.notes[0].exist : "N/A"})` : "Common"}
            </p>
            <p className="item-line">
              <span className="item-title">Industry:</span> {item.craftable || "No Industry"}
            </p>
            <p className="item-line">
              <span className="item-title">Edible Energy:</span> {item.energyGiven || "None"}
            </p>
            <p className="item-line">
              <span className="item-title">Type:</span> {item.category || "None"}
            </p>
            <p className="item-line">
              <span className="item-title">How to Purchase:</span>{" "}
              {Array.isArray(item.purchaseable) && item.purchaseable.length > 0 ? item.purchaseable.join(", ") : "Not purchaseable"}
            </p>
            <p className="item-line">
              <span className="item-title">Market Price:</span> {`${item.marketPrice} (${item.marketPieces?.toLocaleString() || "None"} Listed)`}
            </p>
            {currencyData && (
              <p className="item-line">
                <span className="item-title">Game Price:</span> {`${item.stores?.[0]?.price} ${currencyData.name} `}
                <img src={currencyData.image} alt={currencyData.name} className=" inline w-3 h-3" />
              </p>
            )}
            <p className="item-line">
              <span className="item-title">Discoverability Points:</span> {item.notes?.[0]?.discpts || "None"}
            </p>
            <div className="item-line">
              <span className="item-title">Item Usage: </span>

              {item.itemUses && item.itemUses.length > 0
                ? item.itemUses.map((use, index) => (
                    <div className="grid grid-cols-2 grid-flow-row md:grid-cols-1">
                      <ul key={index} onClick={() => handleOpenModal(use.id)} className="cursor-pointer">
                        <img src={use.achievementImage} alt={use.name} className="h-4 w-4 inline" /> {use.name}
                      </ul>
                    </div>
                  ))
                : "Nothing can be crafted from this item"}
            </div>
          </div>
          <div className="w-full mt-4 md:mt-0">
            <div className="flex flex-wrap justify-center gap-1 md:gap-2">
              {item.type === "Item" && item.recipes?.length > 0 && (
                <button className={`wiki-button ${activeTab === "crafting" ? "depressed" : ""}`} onClick={() => setActiveTab("crafting")}>
                  Crafting
                </button>
              )}

              {item.type === "Item" && item.produce?.length > 0 && (
                <button className={`wiki-button ${activeTab === "produce" ? "depressed" : ""}`} onClick={() => setActiveTab("produce")}>
                  Production
                </button>
              )}

              {item.type === "Item" && item.crop && (
                <button className={`wiki-button ${activeTab === "crop" ? "depressed" : ""}`} onClick={() => setActiveTab("crop")}>
                  Crop
                </button>
              )}

              {item.type === "Item" && item.recipes?.length > 0 && (
                <button className={`wiki-button ${activeTab === "craftMap" ? "depressed" : ""}`} onClick={() => setActiveTab("craftMap")}>
                  Craft Map
                </button>
              )}

              {item.placeObject?.image && (
                <button className={`wiki-button ${activeTab === "sprite" ? "depressed" : ""}`} onClick={() => setActiveTab("sprite")}>
                  Sprites
                </button>
              )}

              <button className={`wiki-button ${activeTab === "market" ? "depressed" : ""}`} onClick={() => setActiveTab("market")}>
                Market
              </button>
            </div>

            <div className="mt-2">
              {activeTab === "crafting" && <ItemCrafting item={item} />}
              {activeTab === "sprite" && <ItemSpritesheet item={item} />}
              {activeTab === "crop" && <ItemCrop item={item} />}
              {activeTab === "craftMap" && <CraftMap item={item} />}
              {activeTab === "produce" && <ItemProduction item={item} />}
              {activeTab === "market" && <ItemMarket item={item} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
